import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useApp } from "../../Context/AppContext";

const SubNavbar = () => {
  const { contextCategories, contextConcerns } = useApp();
  const navigate = useNavigate();

  const [isLoggedIn, setIsLoggedIn] = useState(
    !!localStorage.getItem("authToken")
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isConcernDropdown, setIsConcernDropdown] = useState(false);

  const dropdownRef = useRef(null);
  const dropdownConRef = useRef(null);

  /* ── auth listener ── */
  useEffect(() => {
    const checkLogin = () =>
      setIsLoggedIn(!!localStorage.getItem("authToken"));
    checkLogin();
    window.addEventListener("authChanged", checkLogin);
    return () => window.removeEventListener("authChanged", checkLogin);
  }, []);

  /* ── close dropdowns on outside click ── */
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
      if (dropdownConRef.current && !dropdownConRef.current.contains(e.target)) {
        setIsConcernDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  /* ── handlers ── */
  const handleCategoryClick = (category) => {
    navigate(
      `/shop-by-category/${category.category_name.toLowerCase().replace(/\s+/g, "-")}`,
      { state: { category_id: category.category_id, category_name: category.category_name } }
    );
    setIsDropdownOpen(false);
  };

  const handleConcernClick = (concern) => {
    if (!concern || typeof concern !== "object") return;
    navigate(
      `/shop-by-concern/${concern.name.toLowerCase().replace(/\s+/g, "-")}`,
      { state: { concern_name: concern.name, concern_id: concern.concern_id } }
    );
    setIsConcernDropdown(false);
  };

  const handleQuickOrder = () => navigate("/my-orders");

  /* ── toggle handlers — click to open, click same to close ── */
  const toggleCategory = () => {
    setIsDropdownOpen((prev) => !prev);
    setIsConcernDropdown(false);
  };

  const toggleConcern = () => {
    setIsConcernDropdown((prev) => !prev);
    setIsDropdownOpen(false);
  };

  /* ── NavLink classes ── */
  const navCls = ({ isActive }) =>
    isActive
      ? "relative font-semibold text-[15px] text-white font-poppins whitespace-nowrap after:absolute after:bottom-0 after:left-0 after:w-full after:h-[2.5px] after:bg-[#ff601e] after:rounded-full pb-[3px]"
      : "relative font-normal text-[15px] text-white/75 font-poppins whitespace-nowrap hover:text-white transition-colors duration-150 after:absolute after:bottom-0 after:left-0 after:w-0 after:h-[2.5px] after:bg-[#ff601e] after:rounded-full after:transition-all after:duration-200 hover:after:w-full pb-[3px]";

  /* ── Dropdown panel ── */
  const DropdownPanel = ({ children }) => (
    <div className="absolute left-0 top-[calc(100%+4px)] bg-white border border-[#e2eaea] rounded-2xl shadow-[0_12px_48px_rgba(22,44,42,0.2)] z-[200] w-[480px] overflow-hidden">
      <div className="w-full h-[3px] bg-gradient-to-r from-[#162c2a] via-[#5fbdb3] to-[#ff601e]" />
      <div className="p-5">{children}</div>
    </div>
  );

  return (
    <>
      {/* Spacer */}
      <div className="hidden lg:block h-[52px] mt-[100px]" />

      {/* ── Sub navbar ── */}
      <div className="hidden lg:block fixed top-[100px] left-0 right-0 z-[100]">
        <div
          className="relative w-full h-[52px] shadow-[0_4px_20px_rgba(22,44,42,0.35)]"
          style={{
            background:
              "linear-gradient(90deg, #162c2a 0%, #1e4a46 40%, #284b49 70%, #1e4a46 100%)",
          }}
        >
          {/* Pharmacy cross watermark */}
          <div
            className="absolute inset-0 opacity-[0.04] pointer-events-none"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='17' y='10' width='6' height='20' rx='2' fill='white'/%3E%3Crect x='10' y='17' width='20' height='6' rx='2' fill='white'/%3E%3C/svg%3E")`,
              backgroundRepeat: "repeat",
              backgroundSize: "40px 40px",
            }}
          />
          <div className="absolute left-0 top-0 bottom-0 w-[180px] bg-gradient-to-r from-[#5fbdb3]/10 to-transparent pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-[180px] bg-gradient-to-l from-[#ff601e]/8 to-transparent pointer-events-none" />
          <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#5fbdb3]/40 to-transparent" />

          {/* Content */}
          <div className="relative max-w-[1920px] mx-auto px-10 flex items-center justify-between h-full">

            {/* ── Left: nav links ── */}
            <nav className="flex items-center gap-8">

              <NavLink to="/home" className={navCls}>Home</NavLink>

              {/* Medicines & Categories dropdown */}
              <div className="relative flex items-center h-[52px]" ref={dropdownRef}>
                <button
                  onClick={toggleCategory}
                  className="relative flex items-center gap-1.5 font-normal text-[15px] text-white/75 font-poppins whitespace-nowrap hover:text-white transition-colors duration-150 pb-[3px] group"
                >
                  Medicines
                  <svg
                    className={`w-3.5 h-3.5 transition-transform duration-200 ${isDropdownOpen ? "rotate-180 text-white" : ""}`}
                    viewBox="0 0 16 16" fill="none"
                  >
                    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className={`absolute bottom-0 left-0 h-[2.5px] bg-[#ff601e] rounded-full transition-all duration-200 ${isDropdownOpen ? "w-full" : "w-0 group-hover:w-full"}`} />
                </button>

                {isDropdownOpen && (
                  <DropdownPanel>
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-[#8faba9] font-poppins mb-3.5">
                      Browse Medicine Categories
                    </p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {contextCategories.length > 0 ? (
                        contextCategories.map((cat) => (
                          <button
                            key={cat.category_id}
                            onClick={() => handleCategoryClick(cat)}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#eaf4f3] text-left transition-colors duration-150 group/item"
                          >
                            <div className="w-10 h-10 rounded-xl bg-[#f1f5f5] border border-[#e2eaea] flex items-center justify-center flex-shrink-0 overflow-hidden group-hover/item:border-[#5fbdb3] transition-colors">
                              <img src={cat.image_url} alt={cat.category_name} className="w-7 h-7 object-contain" />
                            </div>
                            <span className="text-[13.5px] font-medium font-poppins text-[#374747] group-hover/item:text-[#1e4a46] leading-snug break-words transition-colors">
                              {cat.category_name}
                            </span>
                          </button>
                        ))
                      ) : (
                        <p className="col-span-2 text-[14px] text-[#8faba9] font-poppins py-2">
                          Loading categories…
                        </p>
                      )}
                    </div>
                  </DropdownPanel>
                )}
              </div>

              <NavLink to="/featured-products" className={navCls}>New Arrivals</NavLink>
              <NavLink to="/shopbrand" className={navCls}>Pharma Brands</NavLink>

              {/* Health Concerns dropdown */}
              <div className="relative flex items-center h-[52px]" ref={dropdownConRef}>
                <button
                  onClick={toggleConcern}
                  className="relative flex items-center gap-1.5 font-normal text-[15px] text-white/75 font-poppins whitespace-nowrap hover:text-white transition-colors duration-150 pb-[3px] group"
                >
                  Health Concerns
                  <svg
                    className={`w-3.5 h-3.5 transition-transform duration-200 ${isConcernDropdown ? "rotate-180 text-white" : ""}`}
                    viewBox="0 0 16 16" fill="none"
                  >
                    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className={`absolute bottom-0 left-0 h-[2.5px] bg-[#ff601e] rounded-full transition-all duration-200 ${isConcernDropdown ? "w-full" : "w-0 group-hover:w-full"}`} />
                </button>

                {isConcernDropdown && (
                  <DropdownPanel>
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-[#8faba9] font-poppins mb-3.5">
                      Shop by Health Concern
                    </p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {contextConcerns?.length > 0 ? (
                        contextConcerns.map((concernItem) => (
                          <button
                            key={concernItem.concern_id}
                            onClick={(e) => {
                              e.preventDefault();
                              handleConcernClick(concernItem);
                            }}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#eaf4f3] text-left transition-colors duration-150 group/item"
                          >
                            <div className="w-10 h-10 rounded-full bg-[#f1f5f5] border border-[#e2eaea] flex items-center justify-center flex-shrink-0 overflow-hidden group-hover/item:border-[#5fbdb3] transition-colors">
                              <img src={concernItem.image_url} alt={concernItem.name} className="w-7 h-7 object-cover rounded-full" />
                            </div>
                            <span className="text-[13.5px] font-medium font-poppins text-[#374747] group-hover/item:text-[#1e4a46] leading-snug break-words transition-colors">
                              {concernItem.name}
                            </span>
                          </button>
                        ))
                      ) : (
                        <p className="col-span-2 text-[14px] text-[#8faba9] font-poppins py-2">
                          Loading concerns…
                        </p>
                      )}
                    </div>
                  </DropdownPanel>
                )}
              </div>

              <NavLink to="/products" className={navCls}>All Medicines</NavLink>
              <NavLink to="/blogs" className={navCls}>Wellness Guide</NavLink>
              <NavLink to="/about-us" className={navCls}>About Us</NavLink>

            </nav>

            {/* ── Right: Quick Order button ── */}
            {isLoggedIn && (
              <button
                onClick={handleQuickOrder}
                className="flex items-center gap-2 bg-[#ff601e] hover:bg-[#e55519] text-white font-poppins font-semibold text-[14px] px-6 h-[36px] rounded-xl transition-all duration-200 shadow-[0_3px_14px_rgba(255,96,30,0.4)] hover:shadow-[0_5px_20px_rgba(255,96,30,0.55)] hover:-translate-y-px whitespace-nowrap"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                </svg>
                Quick Order
              </button>
            )}

          </div>
        </div>
      </div>
    </>
  );
};

export default SubNavbar;